import { Component, inject, Input, OnChanges, OnDestroy, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { hotelFeature } from '@contler/core/hotel';
import { LanguageModel } from '@contler/translate/models';
import { NavigationService } from '@contler/utils';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { first, forkJoin, map, Subject, takeUntil } from 'rxjs';

import { SelectChipInputModule } from '../forms/select-chip-input/select-chip-input.module';

@Component({
  selector: 'ctr-navbar',
  imports: [MatIconModule, SelectChipInputModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnChanges, OnDestroy {
  @Input() backUrl: string | undefined;
  @Input() homeUrl = '/';
  @Input() backQueryParams: { [key: string]: string } = {};
  @Input() hideBackButton = false;
  @Input() hideMenuButton = false;
  @Input() languages: LanguageModel[] | null = [];
  @Input() selectedLanguage: LanguageModel | undefined | null;
  @Input() showLanguageSelector = false;

  public languageChange = output<LanguageModel>();
  public unsubscribe$ = new Subject<void>();

  private url = '';
  hotelImage: string | undefined = '';

  languageControl = new FormControl();

  private store = inject(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private navigationService = inject(NavigationService);

  constructor() {
    this.store.select(hotelFeature.selectHotel).subscribe((hotel) => {
      this.hotelImage = hotel?.logo;
    });
    const currentNavigation = this.router.currentNavigation();

    forkJoin([
      this.route.url.pipe(
        first(),
        map((segments) => segments.join('/') as string),
      ),
      this.route.queryParams.pipe(
        first(),
        map((params) => params['backUrl']),
      ),
    ]).subscribe(([url, backQuery]) => {
      this.url = url;
      if (currentNavigation?.extras.state) {
        this.navigationService.setNavigation(this.url, currentNavigation.extras.state['backUrl']);
      } else if (backQuery) {
        this.navigationService.setNavigation(this.url, backQuery);
      }
    });

    this.languageControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((code) => {
      const language = this.languages?.find((lang) => lang.code === code);
      if (language) {
        this.languageChange.emit(language);
      }
    });
  }

  ngOnChanges() {
    if (this.selectedLanguage) {
      this.languageControl.setValue(this.selectedLanguage.code);
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  goBack() {
    const backDirection = this.navigationService.getNavigation(this.url);
    if (this.backUrl) {
      window.location.href = this.backUrl;
    } else if (backDirection) {
      this.router.navigate([backDirection], { queryParams: this.backQueryParams });
    } else {
      window.history.back();
    }
  }

  goHome() {
    window.location.href = this.homeUrl;
  }
}
